import React, { useState, useEffect, useRef } from 'react';
import { auth, db } from '../config/firebaseConfig'; // Import Firestore and Firebase config
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'; // Firestore functions
import { useNavigate } from 'react-router-dom';
import '../style/LoginPage.css';
import logo from '../assets/logo.png';
import { collection, query, where, getDocs } from 'firebase/firestore';

/**
 * ParticlesJS is a global function provided by particles.js library
 * It's used to initialize particle animations
 */

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [countdown, setCountdown] = useState(8); // Increased to 10 seconds
  const [cameraError, setCameraError] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotTaken, setSnapshotTaken] = useState(false);
  const [useFallbackMethod, setUseFallbackMethod] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [showKVKKModal, setShowKVKKModal] = useState(false);
  const [kvkkAccepted, setKvkkAccepted] = useState(false);
  const [pendingEmployeeDoc, setPendingEmployeeDoc] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const navigate = useNavigate();

  // Load last logged user's email on component mount
  useEffect(() => {
    const lastLoggedEmail = localStorage.getItem('lastLoggedEmail');
    if (lastLoggedEmail) {
      setEmail(lastLoggedEmail);
    }
  }, []);

  useEffect(() => {
    // Check if particlesJS is available in the window object
    if (typeof window.particlesJS !== 'undefined') {
      window.particlesJS('particles-js', {
        particles: {
          number: { value: 80, density: { enable: true, value_area: 800 } },
          color: { value: "#ffffff" },
          shape: { type: "circle", stroke: { width: 0, color: "#000000" }, polygon: { nb_sides: 5 }, },
          opacity: { value: 0.5, random: false, anim: { enable: false, speed: 1, opacity_min: 0.1, sync: false } },
          size: { value: 3, random: true, anim: { enable: false, speed: 40, size_min: 0.1, sync: false } },
          line_linked: { enable: true, distance: 150, color: "#ffffff", opacity: 0.4, width: 1 },
          move: { enable: true, speed: 6, direction: "none", random: false, straight: false, out_mode: "out", bounce: false, attract: { enable: false, rotateX: 600, rotateY: 1200 } },
        },
        interactivity: {
          detect_on: "canvas",
          events: { onhover: { enable: true, mode: "repulse" }, onclick: { enable: true, mode: "push" }, resize: true },
          modes: { grab: { distance: 400, line_linked: { opacity: 1 } }, bubble: { distance: 400, size: 40, duration: 2, opacity: 8, speed: 3 }, repulse: { distance: 200, duration: 0.4 }, push: { particles_nb: 4 }, remove: { particles_nb: 2 } },
        },
        retina_detect: true
      });
    } else {
      console.warn('particlesJS is not defined. The background animation may not work.');
    }
  }, []);

  // Handle video element once it's mounted and stream is available
  useEffect(() => {
    if (videoRef.current && streamRef.current && showCamera) {
      try {
        videoRef.current.srcObject = streamRef.current;
        
        // Make sure video plays when ready
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(e => {
            console.error("Error playing video:", e);
            setCameraError("Video oynatma hatası: " + e.message);
            // Try fallback method
            setUseFallbackMethod(true);
          });
        };
      } catch (err) {
        console.error("Error setting video source:", err);
        // Try fallback method
        setUseFallbackMethod(true);
      }
    }
  }, [videoRef.current, streamRef.current, showCamera]);

  // Fallback method to display camera feed
  useEffect(() => {
    if (useFallbackMethod && videoRef.current && streamRef.current && showCamera) {
      try {
        // Create a URL from the stream
        const videoURL = window.URL.createObjectURL(streamRef.current);
        videoRef.current.src = videoURL;
        
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(e => {
            console.error("Error playing video with fallback method:", e);
            setCameraError("Fallback video oynatma hatası: " + e.message);
          });
        };
      } catch (err) {
        console.error("Error with fallback method:", err);
        setCameraError("Kamera görüntüsü gösterilemiyor: " + err.message);
      }
    }
  }, [useFallbackMethod, videoRef.current, streamRef.current, showCamera]);

  // Clean up camera stream when component unmounts
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Countdown timer effect
  useEffect(() => {
    let timer;
    if (showCamera && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 800);
    } else if (showCamera && countdown === 0) {
      // When countdown reaches 0, take a snapshot and proceed with login
      takeSnapshot();
      stopCamera();
      proceedWithLogin();
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showCamera, countdown]);

  // Check if camera is supported
  const isCameraSupported = () => {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  };

  const takeSnapshot = () => {
    if (videoRef.current && !snapshotTaken) {
      try {
        // Create canvas if it doesn't exist
        const canvas = canvasRef.current || document.createElement('canvas');
        const video = videoRef.current;
        
        // Set canvas dimensions to match video
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;
        
        // Draw video frame to canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Get data URL from canvas
        const dataUrl = canvas.toDataURL('image/jpeg');
        setSnapshot(dataUrl);
        setSnapshotTaken(true);
        
        // Flash effect to indicate snapshot was taken
        const flashElement = document.createElement('div');
        flashElement.className = 'camera-flash';
        document.body.appendChild(flashElement);
        
        // Remove flash element after animation
        setTimeout(() => {
          if (document.body.contains(flashElement)) {
            document.body.removeChild(flashElement);
          }
        }, 800);
        
        return dataUrl;
      } catch (err) {
        console.error("Error taking snapshot:", err);
        return null;
      }
    }
    return null;
  };

  const startCamera = async () => {
    // Reset any previous errors and snapshot
    setCameraError('');
    setSnapshot(null);
    setSnapshotTaken(false);
    setUseFallbackMethod(false);
    
    // Check if camera is supported
    if (!isCameraSupported()) {
      console.warn('Camera not supported in this browser');
      setCameraError('Kamera bu tarayıcıda desteklenmiyor');
      // Proceed with login without camera
      setTimeout(() => {
        proceedWithLogin();
      }, 1500);
      return;
    }
    
    // First show the camera UI with loading state
    setCameraLoading(true);
    setShowCamera(true);
    setCountdown(5);
    
    try {
      // First stop any existing streams
      if (streamRef.current) {
        stopCamera();
      }
      
      console.log("Requesting camera access...");
      
      // Request camera with more basic settings first
      const constraints = { 
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        },
        audio: false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      console.log("Camera access granted");
      
      // Store the stream reference
      streamRef.current = stream;
      
      if (videoRef.current) {
        try {
          // Try the standard approach first
          videoRef.current.srcObject = stream;
          
          // Make sure video plays when ready
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().catch(e => {
              console.error("Error playing video:", e);
              setUseFallbackMethod(true);
            });
          };
        } catch (e) {
          console.warn("Could not set srcObject directly:", e);
          setUseFallbackMethod(true);
        }
      }
      
      setCameraLoading(false);
      
    } catch (err) {
      console.error('Error accessing camera:', err);
      setCameraLoading(false);
      
      // Set appropriate error message based on the error
      if (err.name === 'NotAllowedError') {
        setCameraError('Kamera erişimi reddedildi');
      } else if (err.name === 'NotFoundError') {
        setCameraError('Kamera bulunamadı');
      } else {
        setCameraError('Kamera erişiminde hata oluştu: ' + err.message);
      }
      
      // Proceed with login after a short delay to show the error
      setTimeout(() => {
        proceedWithLogin();
      }, 1500);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      console.log("Stopping camera stream");
      const tracks = streamRef.current.getTracks();
      tracks.forEach(track => {
        try {
          track.stop();
        } catch (e) {
          console.error("Error stopping track:", e);
        }
      });
      streamRef.current = null;
    }
    
    // Clear video source
    if (videoRef.current) {
      try {
        videoRef.current.srcObject = null;
        videoRef.current.src = '';
      } catch (e) {
        console.error("Error clearing video source:", e);
      }
    }
    
    setShowCamera(false);
    setUseFallbackMethod(false);
  };

  const skipCameraAndLogin = () => {
    // Take a snapshot before skipping if camera is active
    if (showCamera && videoRef.current && !snapshotTaken) {
      takeSnapshot();
    }
    stopCamera();
    proceedWithLogin();
  };

  const proceedWithLogin = async () => {
    try {
      // First authenticate with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Then check if the email exists in Employees collection
      const employeesRef = collection(db, 'Employees');
      const q = query(employeesRef, where('eMail', '==', email));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('User not found in Employees collection');
      }

      // Get the employee document
      const employeeDoc = querySnapshot.docs[0];
      const employeeData = employeeDoc.data();
      
      // Check if employee is active
      if (employeeData.Active !== 1) {
        // Sign out the user since they're not active
        await auth.signOut();
        setError('Giriş Yetkiniz Kaldırılmıştır.');
        setIsLoading(false);
        return;
      }

      // Check if KVKK consent is given
      if (!employeeData.kvkkConsent || employeeData.kvkkConsent !== true) {
        // Show KVKK modal
        setPendingEmployeeDoc(employeeDoc);
        setShowKVKKModal(true);
        setIsLoading(false);
        return;
      }
      
      // Continue with normal login flow
      await completeLogin(employeeDoc, employeeData);
      
    } catch (error) {
      console.error('Login error:', error);
      setError('Geçersiz e-posta veya şifre');
      setIsLoading(false);
    }
  };

  const completeLogin = async (employeeDoc, employeeData) => {
    try {
      // Always store a login history entry; the snapshot is included when
      // available (camera permission may be denied or capture may fail)
      try {
        const loginHistoryRef = collection(db, 'Employees', employeeDoc.id, 'loginHistory');
        await setDoc(doc(loginHistoryRef), {
          timestamp: new Date(),
          snapshot: snapshot || null,
          device: navigator.userAgent
        });

        // Update the employee's last login time
        await updateDoc(doc(db, 'Employees', employeeDoc.id), {
          lastLogin: new Date()
        });
      } catch (historyError) {
        console.error('Error saving login history:', historyError);
        // Continue with login even if history saving fails
      }

      // Store employee ID and data in localStorage or context for later use
      localStorage.setItem('employeeId', employeeDoc.id);
      localStorage.setItem('employeeData', JSON.stringify({
        name: employeeData.eName,
        surname: employeeData.eSurname,
        email: employeeData.eMail,
        // Add other needed fields
      }));

      // Save the email for next login (remember last user)
      localStorage.setItem('lastLoggedEmail', email);

      // Navigate to dashboard with employee data
      navigate('/dashboard', { 
        state: { 
          email: employeeData.eMail, 
          userId: employeeDoc.id,
          name: employeeData.eName
        } 
      });
    } catch (error) {
      console.error('Complete login error:', error);
      setError('Giriş işlemi tamamlanamadı');
      setIsLoading(false);
    }
  };

  const handleKVKKAccept = async () => {
    if (!kvkkAccepted) {
      alert('Lütfen KVKK sözleşmesini onaylayın.');
      return;
    }

    try {
      // Update employee document with KVKK consent
      await updateDoc(doc(db, 'Employees', pendingEmployeeDoc.id), {
        kvkkConsent: true,
        kvkkConsentDate: new Date()
      });

      // Close modal and complete login
      setShowKVKKModal(false);
      const employeeData = pendingEmployeeDoc.data();
      await completeLogin(pendingEmployeeDoc, employeeData);
    } catch (error) {
      console.error('Error saving KVKK consent:', error);
      setError('KVKK onayı kaydedilemedi. Lütfen tekrar deneyin.');
    }
  };

  const handleKVKKReject = () => {
    setShowKVKKModal(false);
    setPendingEmployeeDoc(null);
    setKvkkAccepted(false);
    setError('KVKK sözleşmesini kabul etmeden giriş yapamazsınız.');
    setIsLoading(false);
    // Sign out the user
    auth.signOut();
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetMessage('');
    setIsResetting(true);

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Şifre sıfırlama bağlantısı e-posta adresinize gönderildi. Lütfen e-postanızı kontrol edin.');
      setResetEmail('');
      // Close modal after 3 seconds
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetMessage('');
      }, 3000);
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/user-not-found') {
        setResetError('Bu e-posta adresi ile kayıtlı kullanıcı bulunamadı.');
      } else if (error.code === 'auth/invalid-email') {
        setResetError('Geçersiz e-posta adresi.');
      } else {
        setResetError('Şifre sıfırlama bağlantısı gönderilemedi. Lütfen tekrar deneyin.');
      }
    } finally {
      setIsResetting(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Prevent multiple clicks
    if (isLoading) return;
    
    // Set loading state
    setIsLoading(true);
    
    try {
      // Start camera first
      await startCamera();
    } catch (error) {
      console.error('Error during login:', error);
      setError('Giriş işlemi sırasında bir hata oluştu');
      setIsLoading(false);
    }
  };

  // Render the camera component
  const renderCamera = () => {
    if (!showCamera) return null;

    return (
      <div className="biometric-scanner">
        <div className="scanner-container">
          {useFallbackMethod ? (
            <div className="camera-fallback">
              <div className="fallback-message">
                <div className="fallback-icon">
                  <i className="scanner-icon"></i>
                </div>
                <p>Kamera erişimi sağlanamadı</p>
                <button 
                  className="skip-button"
                  onClick={skipCameraAndLogin}
                >
                  Devam Et
                </button>
              </div>
            </div>
          ) : (
            <>
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline
                muted
                className="camera-feed"
              />
              
              <div className="scanner-ui">
                {/* Scanner Frame */}
                <div className="scanner-frame">
                  <div className="scanner-frame-corner top-left"></div>
                  <div className="scanner-frame-corner top-right"></div>
                  <div className="scanner-frame-corner bottom-left"></div>
                  <div className="scanner-frame-corner bottom-right"></div>
                  <div className="scanner-ring"></div>
                </div>
                
                {/* Scanner Metrics */}
                <div className="scanner-metrics">
                  <div className="metrics-item processing">
                    <div className="metrics-label">Processing...</div>
                    <div className="metrics-bar">
                      <div className="bar-fill"></div>
                    </div>
                  </div>
                  
                  <div className="metrics-item">
                    <div className="metrics-label">Face Detection</div>
                    <div className="metrics-value">Active</div>
                  </div>
                  
                  <div className="metrics-item blinking">
                    <div className="metrics-label">System Status</div>
                    <div className="metrics-value">Secure</div>
                  </div>
                </div>
                
                {/* Facial Points */}
                <div className="facial-points">
                  {[...Array(15)].map((_, i) => (
                    <div 
                      key={i} 
                      className={`point point-${i}`}
                    ></div>
                  ))}
                </div>
                
                {/* Scan Animation */}
                <div className="scan-animation">
                  <div className="scan-line"></div>
                </div>
                
                {/* Data Streams */}
                <div className="data-streams">
                  {[...Array(8)].map((_, i) => (
                    <div 
                      key={i} 
                      className={`data-stream stream-${i}`}
                    ></div>
                  ))}
                </div>
                
                {/* Recognition Status */}
                <div className="recognition-status">
                  <div className="status-container">
                    <div className="status-circle"></div>
                    <div className="status-text">Yüz Tarama: <span className="counter">{countdown}s</span></div>
                  </div>
                </div>
                
                {/* Scanner Overlay */}
                <div className="scanner-overlay">
                  <div className="overlay-grid"></div>
                </div>
              </div>
              
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </>
          )}
        </div>
        
        <div className="scanner-controls">
          <button 
            className="skip-button"
            onClick={skipCameraAndLogin}
          >
            Şimdi Giriş Yap
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="login-page login-page-full">
      <div id="particles-js" className="particles-container"></div>
      <div className="login-container">
        <div className="login-header">
            <img src={logo} alt="HCC HABCARE CRO Logo" className="login-logo" />
        </div>
        
        {renderCamera()}
        
        {cameraLoading && (
          <div className="camera-loading">
            <p>Kamera açılıyor...</p>
          </div>
        )}
        
        {cameraError && !showCamera && (
          <div className="camera-error">
            <p>{cameraError}</p>
          </div>
        )}
        
        <form onSubmit={handleLogin} className="login-form">
          <input
            className="login-input"
            type="email"
            placeholder="Kullanıcı Adı"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isLoading}
          />
          <input
            className="login-input"
            type="password"
            placeholder="Şifre"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
          />
          {error && <p className="login-error">{error}</p>}
          <button 
            className={`login-button ${isLoading ? 'loading' : ''}`} 
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="loading-spinner"></span>
                Giriş Yapılıyor...
              </>
            ) : (
              'Giriş Yap'
            )}
          </button>
          <button
            type="button"
            className="forgot-password-link"
            onClick={() => setShowForgotPassword(true)}
            disabled={isLoading}
          >
            Şifremi Unuttum
          </button>
        </form>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="forgot-password-modal-overlay" onClick={() => setShowForgotPassword(false)}>
            <div className="forgot-password-modal" onClick={(e) => e.stopPropagation()}>
              <h2>Şifremi Unuttum</h2>
              <p>E-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim.</p>
              <form onSubmit={handleForgotPassword}>
                <input
                  type="email"
                  placeholder="E-posta Adresi"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  disabled={isResetting}
                  className="reset-email-input"
                />
                {resetError && <p className="reset-error">{resetError}</p>}
                {resetMessage && <p className="reset-success">{resetMessage}</p>}
                <div className="modal-buttons">
                  <button
                    type="submit"
                    className="reset-button"
                    disabled={isResetting}
                  >
                    {isResetting ? 'Gönderiliyor...' : 'Gönder'}
                  </button>
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                      setResetError('');
                      setResetMessage('');
                    }}
                    disabled={isResetting}
                  >
                    İptal
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* KVKK Consent Modal */}
        {showKVKKModal && (
          <div className="kvkk-modal-overlay">
            <div className="kvkk-modal" onClick={(e) => e.stopPropagation()}>
              <h2>KVKK Aydınlatma Metni ve Açık Rıza Beyanı</h2>
              <div className="kvkk-content">
                <h3>Kişisel Verilerin Korunması</h3>
                <p>
                  6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") uyarınca, 
                  kişisel verileriniz HCC HABCARE CRO tarafından aşağıda açıklanan 
                  kapsamda işlenebilecektir.
                </p>
                
                <h4>Veri Sorumlusu</h4>
                <p>
                  Kişisel verileriniz, veri sorumlusu sıfatıyla HCC HABCARE CRO 
                  tarafından aşağıda açıklanan kapsamda işlenebilecektir.
                </p>
                
                <h4>İşlenen Kişisel Veriler</h4>
                <ul>
                  <li>Kimlik bilgileri (ad, soyad, TC kimlik numarası)</li>
                  <li>İletişim bilgileri (e-posta, telefon, adres)</li>
                  <li>Çalışan bilgileri (departman, pozisyon, yetkilendirme)</li>
                  <li>Giriş kayıtları ve sistem kullanım bilgileri</li>
                  <li>Biyometrik veriler (yüz tanıma görüntüleri)</li>
                </ul>
                
                <h4>Kişisel Verilerin İşlenme Amacı</h4>
                <ul>
                  <li>İş sözleşmesinin kurulması ve ifası</li>
                  <li>Çalışan yönetimi ve insan kaynakları süreçleri</li>
                  <li>Sistem güvenliği ve erişim kontrolü</li>
                  <li>Yasal yükümlülüklerin yerine getirilmesi</li>
                  <li>İş sağlığı ve güvenliği uygulamaları</li>
                </ul>
                
                <h4>Kişisel Verilerin Aktarılması</h4>
                <p>
                  Kişisel verileriniz, yukarıda belirtilen amaçlar doğrultusunda, 
                  yasal yükümlülüklerimizi yerine getirmek, iş sözleşmesinin ifası 
                  ve meşru menfaatlerimiz kapsamında gerekli olduğu ölçüde üçüncü 
                  kişilere aktarılabilecektir.
                </p>
                
                <h4>Haklarınız</h4>
                <p>KVKK'nın 11. maddesi uyarınca, şirketimize başvurarak:</p>
                <ul>
                  <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme,</li>
                  <li>İşlenmişse buna ilişkin bilgi talep etme,</li>
                  <li>İşlenme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme,</li>
                  <li>Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme,</li>
                  <li>Eksik veya yanlış işlenmiş olması hâlinde bunların düzeltilmesini isteme,</li>
                  <li>İlgili mevzuatta öngörülen şartlar çerçevesinde silinmesini veya yok edilmesini isteme,</li>
                  <li>Aktarıldığı üçüncü kişilere yukarıdaki işlemlerin bildirilmesini isteme,</li>
                  <li>Münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle aleyhinize bir sonucun ortaya çıkmasına itiraz etme,</li>
                  <li>Kanuna aykırı olarak işlenmesi sebebiyle zarara uğramanız hâlinde zararın giderilmesini talep etme haklarına sahipsiniz.</li>
                </ul>
                
                <div className="kvkk-checkbox">
                  <input
                    type="checkbox"
                    id="kvkkConsent"
                    checked={kvkkAccepted}
                    onChange={(e) => setKvkkAccepted(e.target.checked)}
                  />
                  <label htmlFor="kvkkConsent">
                    KVKK Aydınlatma Metnini okudum, anladım ve kişisel verilerimin 
                    yukarıda belirtilen kapsamda işlenmesine açık rıza gösteriyorum.
                  </label>
                </div>
              </div>
              
              <div className="kvkk-buttons">
                <button
                  type="button"
                  className="kvkk-reject-button"
                  onClick={handleKVKKReject}
                >
                  Kabul Etmiyorum
                </button>
                <button
                  type="button"
                  className="kvkk-accept-button"
                  onClick={handleKVKKAccept}
                  disabled={!kvkkAccepted}
                >
                  Kabul Ediyorum
                </button>
              </div>
            </div>
          </div>
        )}

        <p className="login-footer">
          ∞ – {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
};

export default LoginPage;